// Main App Component
// Application routing and context providers

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import React, { Suspense } from 'react';
import { AuthProvider, ForcePasswordChangeModal, useAuth } from '@/features/auth';
import { CourseProvider } from './providers/CourseContext';
import { ModalProvider } from './providers/ModalContext';
import { ErrorBoundary, PageLoadingSpinner, CookieConsentModal } from '@/shared/ui';
import { ProtectedRoute, PublicRoute, UserAccessGuard, ComplianceRequiredRoute, ParentalConsentRequiredRoute } from './guards';
import { PUBLIC_ROUTES, PROTECTED_ROUTES, ADMIN_ROUTES, INSTRUCTOR_ROUTES } from '@/shared/constants';
import { StagingPasswordGate } from './StagingPasswordGate';

import '@/shared/assets/styles/global.css';
import '@/shared/assets/styles/theme.css';
import '@/shared/assets/styles/animations.css';

const MainLayout = React.lazy(() => import('@/widgets/Layout').then(m => ({ default: m.MainLayout })));
const DashboardLayout = React.lazy(() => import('@/widgets/Layout').then(m => ({ default: m.DashboardLayout })));
const AuthLayout = React.lazy(() => import('@/widgets/Layout').then(m => ({ default: m.AuthLayout })));
const AdminLayout = React.lazy(() => import('@/widgets/Layout').then(m => ({ default: m.AdminLayout })));
const ParentVerificationLayout = React.lazy(() => import('@/widgets/Layout').then(m => ({ default: m.ParentVerificationLayout })));

const HomePage = React.lazy(() => import('@/features/marketing').then(m => ({ default: m.HomePage })));
const CoursesPage = React.lazy(() => import('@/features/courses').then(m => ({ default: m.CoursesPage })));
const AboutPage = React.lazy(() => import('@/features/marketing').then(m => ({ default: m.AboutPage })));
const ContactPage = React.lazy(() => import('@/features/marketing').then(m => ({ default: m.ContactPage })));
const PrivacyPolicy = React.lazy(() => import('@/features/marketing').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = React.lazy(() => import('@/features/marketing').then(m => ({ default: m.TermsOfService })));
const RefundPolicy = React.lazy(() => import('@/features/marketing').then(m => ({ default: m.RefundPolicy })));
const CookiePolicy = React.lazy(() => import('@/features/marketing').then(m => ({ default: m.CookiePolicy })));
const LoginPage = React.lazy(() => import('@/features/auth').then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import('@/features/auth').then(m => ({ default: m.RegisterPage })));
const EmailVerificationPage = React.lazy(() => import('@/features/auth').then(m => ({ default: m.EmailVerificationPage })));
const ParentVerificationPage = React.lazy(() => import('@/features/auth').then(m => ({ default: m.ParentVerificationPage })));
const ForgotPasswordPage = React.lazy(() => import('@/features/auth').then(m => ({ default: m.ForgotPasswordPage })));
const DashboardPage = React.lazy(() => import('@/features/dashboard').then(m => ({ default: m.DashboardPage })));
// import { DashboardPage } from '@/features/dashboard';
const MyCoursesPage = React.lazy(() => import('@/features/courses').then(m => ({ default: m.MyCoursesPage })));
const CourseDetailPage = React.lazy(() => import('@/features/courses').then(m => ({ default: m.CourseDetailPage })));
const LessonPage = React.lazy(() => import('@/features/courses').then(m => ({ default: m.LessonPage })));
const CoursePlayerPage = React.lazy(() => import('@/features/courses').then(m => ({ default: m.CoursePlayerPage })));
const ProgressPage = React.lazy(() => import('@/features/courses').then(m => ({ default: m.ProgressPage })));
const ProfilePage = React.lazy(() => import('@/features/dashboard').then(m => ({ default: m.ProfilePage })));
const CertificatesPage = React.lazy(() => import('@/features/certificates').then(m => ({ default: m.CertificatesPage }))); // Kept for CERTIFICATE_VIEW if needed
const PaymentHistoryPage = React.lazy(() => import('@/features/dashboard').then(m => ({ default: m.PaymentHistoryPage })));
const PaymentSuccessPage = React.lazy(() => import('@/features/dashboard').then(m => ({ default: m.PaymentSuccessPage })));
const AdminDashboard = React.lazy(() => import('@/features/admin').then(m => ({ default: m.AdminDashboard })));
const AuditLogsPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.AuditLogsPage })));
const SecurityManagementPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.SecurityManagementPage })));
const AnalyticsPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.AnalyticsPage })));
const PerformanceDashboard = React.lazy(() => import('@/features/admin').then(m => ({ default: m.PerformanceDashboard })));
const WebVitalsDashboard = React.lazy(() => import('@/features/admin').then(m => ({ default: m.WebVitalsDashboard })));
const UsersPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.UsersPage })));
const EnrollmentManagementPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.EnrollmentManagementPage })));
const SchedulingPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.SchedulingPage })));
const ComplianceReportsPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.ComplianceReportsPage })));
const DETSExportPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.DETSExportPage })));
const AdminSupportPage = React.lazy(() => import('@/features/admin/pages/Support/AdminSupportPage'));
const AdminCoursesPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.AdminCoursesPage })));
const AdminLessonsPage = React.lazy(() => import('@/features/admin').then(m => ({ default: m.AdminLessonsPage })));
const NotFoundPage = React.lazy(() => import('@/features/core').then(m => ({ default: m.NotFoundPage })));
const ExamPage = React.lazy(() => import('@/features/courses').then(m => ({ default: m.ExamPage })));

export function AppRoutes() {
  const { showPasswordChangeModal, setShowPasswordChangeModal } = useAuth();

  return (
    <>
      <ForcePasswordChangeModal
        isOpen={showPasswordChangeModal}
        onComplete={() => setShowPasswordChangeModal(false)}
      />
      <Routes>
        {/* Public Routes */}
        <Route path={PUBLIC_ROUTES.HOME} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <HomePage />
            </MainLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.COURSES} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <CoursesPage />
            </MainLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.ABOUT} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <AboutPage />
            </MainLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.CONTACT} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <ContactPage />
            </MainLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.PRIVACY_POLICY} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <PrivacyPolicy />
            </MainLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.TERMS_OF_SERVICE} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <TermsOfService />
            </MainLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.REFUND_POLICY} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <RefundPolicy />
            </MainLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.COOKIE_POLICY} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <CookiePolicy />
            </MainLayout>
          </Suspense>
        } />

        {/* Auth Routes */}
        <Route path={PUBLIC_ROUTES.LOGIN} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <PublicRoute>
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            </PublicRoute>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.REGISTER} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <PublicRoute>
              <AuthLayout>
                <RegisterPage />
              </AuthLayout>
            </PublicRoute>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.EMAIL_VERIFICATION} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <AuthLayout>
              <EmailVerificationPage />
            </AuthLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.PARENT_VERIFICATION} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ParentVerificationLayout>
              <ParentVerificationPage />
            </ParentVerificationLayout>
          </Suspense>
        } />

        <Route path={PUBLIC_ROUTES.FORGOT_PASSWORD} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <PublicRoute>
              <AuthLayout>
                <ForgotPasswordPage />
              </AuthLayout>
            </PublicRoute>
          </Suspense>
        } />

        {/* Protected Routes */}
        <Route path={PROTECTED_ROUTES.DASHBOARD} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <DashboardLayout>
                <DashboardPage />
              </DashboardLayout>
            </ProtectedRoute>
          </Suspense>
        } />


        <Route path={PROTECTED_ROUTES.COURSE_DETAIL} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <ParentalConsentRequiredRoute>
                <DashboardLayout>
                  <CourseDetailPage />
                </DashboardLayout>
              </ParentalConsentRequiredRoute>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={PROTECTED_ROUTES.LESSON} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <ParentalConsentRequiredRoute>
                <DashboardLayout>
                  <LessonPage />
                </DashboardLayout>
              </ParentalConsentRequiredRoute>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={PROTECTED_ROUTES.COURSE_PLAYER} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <ParentalConsentRequiredRoute>
                <ComplianceRequiredRoute>
                  <CoursePlayerPage />
                </ComplianceRequiredRoute>
              </ParentalConsentRequiredRoute>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={PROTECTED_ROUTES.EXAM} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <ParentalConsentRequiredRoute>
                <ComplianceRequiredRoute>
                  <DashboardLayout>
                    <ExamPage />
                  </DashboardLayout>
                </ComplianceRequiredRoute>
              </ParentalConsentRequiredRoute>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={PROTECTED_ROUTES.PROFILE_VIEW} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <UserAccessGuard accessType="profile">
                <DashboardLayout>
                  <ProfilePage />
                </DashboardLayout>
              </UserAccessGuard>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={PROTECTED_ROUTES.PROFILE} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <DashboardLayout>
                <ProfilePage />
              </DashboardLayout>
            </ProtectedRoute>
          </Suspense>
        } />



        <Route path={PROTECTED_ROUTES.CERTIFICATE_VIEW} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <UserAccessGuard accessType="certificate">
                <DashboardLayout>
                  <CertificatesPage />
                </DashboardLayout>
              </UserAccessGuard>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={PROTECTED_ROUTES.PAYMENT_SUCCESS} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <PaymentSuccessPage />
            </ProtectedRoute>
          </Suspense>
        } />

        {/* Admin Routes */}
        <Route path={ADMIN_ROUTES.ADMIN_DASHBOARD} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.AUDIT_LOGS} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <AuditLogsPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.SECURITY_MANAGEMENT} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <SecurityManagementPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.MANAGE_USERS} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <UsersPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.MANAGE_ENROLLMENTS} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <EnrollmentManagementPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.ANALYTICS} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <AnalyticsPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.PERFORMANCE} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <PerformanceDashboard />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.WEB_VITALS} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <WebVitalsDashboard />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.SCHEDULING} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <SchedulingPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.COMPLIANCE_REPORTS} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <ComplianceReportsPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.DETS_EXPORT} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <DETSExportPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.SUPPORT} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <AdminSupportPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.MANAGE_COURSES} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <AdminCoursesPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={ADMIN_ROUTES.MANAGE_LESSONS} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <AdminLessonsPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        <Route path={INSTRUCTOR_ROUTES.INSTRUCTOR_DASHBOARD} element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <ProtectedRoute>
              <AdminLayout>
                <SchedulingPage />
              </AdminLayout>
            </ProtectedRoute>
          </Suspense>
        } />

        {/* 404 Route */}
        <Route path="*" element={
          <Suspense fallback={<PageLoadingSpinner />}>
            <MainLayout>
              <NotFoundPage />
            </MainLayout>
          </Suspense>
        } />
      </Routes>
    </>
  );
}

const App: React.FC = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ErrorBoundary>
        <AuthProvider>
          <CourseProvider>
            <ModalProvider>
              <StagingPasswordGate>
                <AppRoutes />
                <CookieConsentModal />
              </StagingPasswordGate>
            </ModalProvider>
          </CourseProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
};

export default App;
